import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Eye, CheckCircle2, XCircle } from "lucide-react";
import { OrderAdminListItem } from "@/services/admin-order.service";

interface OrderActionsDropdownProps {
  order: OrderAdminListItem;
  onComplete?: () => void;
  onCancel?: () => void;
}

/**
 * Row actions for the order table.
 *
 * No "View Payment" / "View Invoice" items: the list endpoint (`OrderAdminListDto`) carries neither
 * id, so those items could never render. The order detail page links both.
 */
export function OrderActionsDropdown({
  order,
  onComplete,
  onCancel,
}: OrderActionsDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const canComplete = order.status === "Paid" || order.status === "Processing";
  // Matches AdminCancelOrderCommandHandler's layer-1 guard (see AdminOrderDetailPage's canCancel
  // comment): the backend always refuses with a 409 past Pending, so offering the item on
  // Paid/Processing/etc. orders only earned a conflict the operator had no way to see coming.
  const canCancel = order.status === "Draft" || order.status === "Pending";

  const handleViewDetails = () => {
    navigate(`/admin/orders/${order.id}`);
    setOpen(false);
  };

  const handleComplete = () => {
    onComplete?.();
    setOpen(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={handleViewDetails}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>

        {(canComplete || canCancel) && <DropdownMenuSeparator />}

        {canComplete && onComplete && (
          <DropdownMenuItem onClick={handleComplete}>
            <CheckCircle2 className="mr-2 h-4 w-4 text-ok-ink" />
            Complete Order
          </DropdownMenuItem>
        )}

        {canCancel && onCancel && (
          <DropdownMenuItem onClick={handleCancel} className="text-destructive-ink">
            <XCircle className="mr-2 h-4 w-4" />
            Cancel Order
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
